import type { GrupoCatalogo, ProdutoCatalogo } from '../api'
import type { ItemCarrinho } from '../store/carrinho-slice'
import { temPassos } from '../components/catalogo-kiosk/passos'

/**
 * Sugestão de acompanhamento do "Pode ser que você goste" do carrinho do celular.
 * O "Quer acompanhar?" do kiosk (totem e tablet) usa a mesma regra, mas está
 * desligado desde 09/10/2026 (ver `components/catalogo-kiosk/use-cardapio-kiosk.tsx`).
 *
 * Sem configuração nova: usa o `tipo` do grupo, que a loja já preenche no
 * cadastro (é o mesmo campo que define o ícone da categoria). Loja sem grupo de
 * bebida ou sobremesa cadastrado simplesmente não recebe sugestão — não há o
 * que inventar, e nada quebra.
 */
const TIPOS_ACOMPANHAMENTO = new Set([
  'refrigerantes',
  'outrasBebidas',
  'sucos',
  'cervejas',
  'chop',
  'cafes',
  'sobremesas',
  'sorvetes',
  'cakes',
]);

const MAX_SUGESTOES = 4;

export interface OpcoesSugestao {
  /** Só produto que entra no carrinho com um toque: sem passos e não pesável. */
  somenteSemEscolha?: boolean
}

function ehAcompanhamento(grupo: GrupoCatalogo): boolean {
  return TIPOS_ACOMPANHAMENTO.has((grupo.tipo ?? '').trim());
}

/** O produto pertence a algum grupo de bebida/sobremesa? */
export function ehProdutoAcompanhamento(grupos: GrupoCatalogo[], idProduto: number): boolean {
  return grupos.some((g) => ehAcompanhamento(g) && g.produtos.some((p) => p.id === idProduto));
}

/** Produto que pede alguma escolha antes de ir ao carrinho (passo ou peso). */
function precisaDeEscolha(produto: ProdutoCatalogo): boolean {
  return temPassos(produto) || produto.pesavel
}

/**
 * Sugere até quatro acompanhamentos: destaques primeiro, depois os mais baratos
 * (a ideia é um "leva junto" fácil de aceitar). Só entra produto com preço.
 */
export function sugerirAcompanhamentos(
  grupos: GrupoCatalogo[],
  itensNoCarrinho: ItemCarrinho[],
  opcoes: OpcoesSugestao = {},
): ProdutoCatalogo[] {
  const jaNoCarrinho = new Set(itensNoCarrinho.map((i) => i.idProduto));
  const vistos = new Set<number>();
  const candidatos: ProdutoCatalogo[] = [];

  for (const grupo of grupos) {
    if (!ehAcompanhamento(grupo)) continue;

    for (const produto of grupo.produtos) {
      if (vistos.has(produto.id) || jaNoCarrinho.has(produto.id) || produto.valor <= 0) continue;
      if (opcoes.somenteSemEscolha && precisaDeEscolha(produto)) continue

      vistos.add(produto.id);
      candidatos.push(produto);
    }
  }

  return candidatos
    .sort((a, b) => Number(b.destaqueCardapio) - Number(a.destaqueCardapio) || a.valor - b.valor)
    .slice(0, MAX_SUGESTOES);
}

/**
 * Vale perguntar? Só quando o cliente acabou de pôr algo que não é bebida nem
 * sobremesa e ainda não tem acompanhamento no carrinho — perguntar a cada item
 * viraria estorvo.
 */
export function devePerguntar(
  grupos: GrupoCatalogo[],
  itensNoCarrinho: ItemCarrinho[],
  idProdutoAdicionado: number,
): boolean {
  if (ehProdutoAcompanhamento(grupos, idProdutoAdicionado)) return false;

  const jaTem = itensNoCarrinho.some((i) => ehProdutoAcompanhamento(grupos, i.idProduto));
  return !jaTem;
}
